import { Injectable } from '@angular/core';
import { PluginType } from '@meta-press/shared-plugins';
import { IGameGenerationService } from './base/game-generation.interface';
import { MultiChoiceGenerationService } from './multi-choice-generation.service';
import { DetectiveCaseStudyGenerationService } from './detective-case-study-generation.service';
import { StudyMaterialSummaryGenerationService } from './study-material-summary-generation.service';
import { TemplateGenerationService } from './template-generation.service';
import { ArgumentationGenerationService } from './argumentation-generation.service';
import { SequenceOrderingGenerationService } from './sequence-ordering-generation.service';
import { PredictionTriviaGenerationService } from './prediction-trivia-generation.service';
import { FAQGenerationService } from './faq-generation.service';
import { DigestGenerationService } from './digest-generation.service';
import { CrosswordPuzzleGenerationService } from './crossword-puzzle-generation.service';
import { DetectiveGenerationService } from './detective-generation.service';
import { WordGuessGenerationService } from './word-guess-generation.service';
import { MiniCryptoGenerationService } from './mini-crypto-generation.service';
import { WordMazeGenerationService } from './word-maze-generation.service';
/**
 * Registry service for managing game-specific generation services
 */
@Injectable({
	providedIn: 'root',
})
export class GameGenerationRegistryService {
	private services = new Map<PluginType, IGameGenerationService>();

	constructor(
		private multiChoiceService: MultiChoiceGenerationService,
		private detectiveCaseStudyService: DetectiveCaseStudyGenerationService,
		private detectiveService: DetectiveGenerationService,
		private studyMaterialSummaryService: StudyMaterialSummaryGenerationService,
		private templateService: TemplateGenerationService,
		private argumentationService: ArgumentationGenerationService,
		private sequenceOrderingService: SequenceOrderingGenerationService,
		private predictionTriviaService: PredictionTriviaGenerationService,
		private faqService: FAQGenerationService,
		private digestService: DigestGenerationService,
		private crosswordPuzzleService: CrosswordPuzzleGenerationService,
		private wordGuessService: WordGuessGenerationService,
		private miniCryptoService: MiniCryptoGenerationService,
		private wordMazeService: WordMazeGenerationService,
	) {
		this.registerServices();
	}

	/**
	 * Register all available game generation services
	 */
	private registerServices(): void {
		this.services.set(PluginType.MultiChoice, this.multiChoiceService);
		this.services.set(
			PluginType.DetectiveCaseStudy,
			this.detectiveCaseStudyService,
		);
		this.services.set(PluginType.Detective, this.detectiveService);
		this.services.set(
			PluginType.StudyMaterialSummary,
			this.studyMaterialSummaryService,
		);
		this.services.set(PluginType.Template, this.templateService);
		this.services.set(PluginType.Argumentation, this.argumentationService);
		this.services.set(
			PluginType.SequenceOrdering,
			this.sequenceOrderingService,
		);
		this.services.set(
			PluginType.PredictionTrivia,
			this.predictionTriviaService,
		);
		this.services.set(PluginType.FAQ, this.faqService);
		this.services.set(PluginType.Digest, this.digestService);
		this.services.set(
			PluginType.CrosswordPuzzle,
			this.crosswordPuzzleService,
		);
		this.services.set(PluginType.WordGuess, this.wordGuessService);
		this.services.set(PluginType.MiniCrypto, this.miniCryptoService);
		this.services.set(PluginType.WordMaze, this.wordMazeService);
	}

	/**
	 * Get a game generation service for a specific plugin type
	 * @param pluginType The plugin type
	 * @returns The corresponding generation service
	 * @throws Error if no service is registered for the plugin type
	 */
	getService(pluginType: PluginType): IGameGenerationService {
		const service = this.services.get(pluginType);
		if (!service) {
			throw new Error(
				`No generation service registered for plugin type: ${pluginType}`,
			);
		}
		return service;
	}

	/**
	 * Check if a service is registered for a plugin type
	 * @param pluginType The plugin type to check
	 * @returns True if a service is registered
	 */
	hasService(pluginType: PluginType): boolean {
		return this.services.has(pluginType);
	}

	/**
	 * Get all registered plugin types
	 * @returns Array of registered plugin types
	 */
	getRegisteredTypes(): PluginType[] {
		return Array.from(this.services.keys());
	}
}
